const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';
const RECAPTCHA_LOAD_TIMEOUT_MS = 10_000;
const RECAPTCHA_EXECUTE_TIMEOUT_MS = 10_000;
const RECAPTCHA_POLL_INTERVAL_MS = 50;

type RecaptchaWidgetId = string | number;

interface ReCaptchaV3 {
  ready: (cb: () => void) => void;
  render: (
    container: HTMLElement,
    options: { sitekey: string; size: 'invisible' },
  ) => RecaptchaWidgetId;
  execute: (widgetId: RecaptchaWidgetId, options: { action: string }) => Promise<string>;
}

type RecaptchaGlobal = typeof globalThis & {
  grecaptcha?: ReCaptchaV3;
};

const isCypressRun = (): boolean => 'Cypress' in globalThis;
const isBrowser = (): boolean => globalThis.document !== undefined;
const getRecaptcha = (): ReCaptchaV3 | undefined => (globalThis as RecaptchaGlobal).grecaptcha;

export const getRecaptchaScriptUrl = (): string => RECAPTCHA_SCRIPT_URL;

let recaptchaScriptPromise: Promise<void> | null = null;
const recaptchaWidgetIds = new Map<string, RecaptchaWidgetId>();
const recaptchaContainers = new Map<string, HTMLElement>();
let recaptchaExecutionQueue = Promise.resolve();

const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
};

const waitForGrecaptcha = (): Promise<ReCaptchaV3> =>
  new Promise((resolve, reject) => {
    const timeoutAt = Date.now() + RECAPTCHA_LOAD_TIMEOUT_MS;

    const poll = (): void => {
      const recaptcha = getRecaptcha();
      if (recaptcha) {
        resolve(recaptcha);
        return;
      }

      if (Date.now() >= timeoutAt) {
        reject(new Error('Timed out waiting for reCAPTCHA to load'));
        return;
      }

      setTimeout(poll, RECAPTCHA_POLL_INTERVAL_MS);
    };

    poll();
  });

const waitForGrecaptchaReady = (recaptcha: ReCaptchaV3): Promise<void> =>
  withTimeout(
    new Promise((resolve) => {
      recaptcha.ready(resolve);
    }),
    RECAPTCHA_LOAD_TIMEOUT_MS,
    'Timed out waiting for reCAPTCHA to be ready',
  );

const injectRecaptchaScript = (locale: string): Promise<void> => {
  if (recaptchaScriptPromise !== null) {
    return recaptchaScriptPromise;
  }

  const scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = globalThis.document.querySelector('script[src*="recaptcha/api.js"]');
    if (existingScript) {
      resolve();
      return;
    }

    const script = globalThis.document.createElement('script');
    script.src = `${RECAPTCHA_SCRIPT_URL}?render=explicit&hl=${locale}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    globalThis.document.head.appendChild(script);
  });

  recaptchaScriptPromise = withTimeout(
    scriptLoadPromise,
    RECAPTCHA_LOAD_TIMEOUT_MS,
    'Timed out loading reCAPTCHA script',
  ).catch((error: unknown) => {
    recaptchaScriptPromise = null;
    throw error;
  });

  return recaptchaScriptPromise;
};

const getRecaptchaContainer = (siteKey: string): HTMLElement => {
  const existingContainer = recaptchaContainers.get(siteKey);
  if (existingContainer && globalThis.document.body.contains(existingContainer)) {
    return existingContainer;
  }

  const container = globalThis.document.createElement('div');
  container.style.position = 'absolute';
  container.style.width = '0';
  container.style.height = '0';
  container.style.overflow = 'hidden';
  container.style.pointerEvents = 'none';
  globalThis.document.body.appendChild(container);
  recaptchaContainers.set(siteKey, container);

  return container;
};

const getRecaptchaWidgetId = async (
  recaptcha: ReCaptchaV3,
  siteKey: string,
): Promise<RecaptchaWidgetId> => {
  const existingWidgetId = recaptchaWidgetIds.get(siteKey);
  if (existingWidgetId !== undefined) {
    return existingWidgetId;
  }

  await waitForGrecaptchaReady(recaptcha);

  const container = getRecaptchaContainer(siteKey);
  const widgetId = recaptcha.render(container, { sitekey: siteKey, size: 'invisible' });
  recaptchaWidgetIds.set(siteKey, widgetId);

  return widgetId;
};

const executeRecaptcha = async (
  recaptcha: ReCaptchaV3,
  widgetId: RecaptchaWidgetId,
  action: string,
): Promise<string> => {
  const execute = async (): Promise<string> => {
    const token = await withTimeout(
      recaptcha.execute(widgetId, { action }),
      RECAPTCHA_EXECUTE_TIMEOUT_MS,
      'Timed out executing reCAPTCHA',
    );
    return token || '';
  };

  const execution = recaptchaExecutionQueue.then(execute, execute);
  recaptchaExecutionQueue = execution.then(
    () => undefined,
    () => undefined,
  );

  return execution;
};

const setRecaptchaBadgeVisibility = (visibility: 'hidden' | 'visible'): void => {
  globalThis.document.querySelectorAll<HTMLElement>('.grecaptcha-badge').forEach((badge) => {
    badge.style.visibility = visibility;
  });
};

export const getRecaptchaToken = async (
  siteKey: string,
  locale: string,
  action: string,
): Promise<string> => {
  if (isCypressRun()) {
    return siteKey;
  }

  if (!isBrowser()) {
    throw new Error('reCAPTCHA is only available in the browser');
  }

  await injectRecaptchaScript(locale);

  const recaptcha = await waitForGrecaptcha();
  const widgetId = await getRecaptchaWidgetId(recaptcha, siteKey);
  return executeRecaptcha(recaptcha, widgetId, action);
};

export const showRecaptchaBadge = (): void => {
  setRecaptchaBadgeVisibility('visible');
};

export const hideRecaptchaBadge = (): void => {
  setRecaptchaBadgeVisibility('hidden');
};
